import React from 'react';
import { X, MapPin, Clock, Users, Calendar as CalendarIcon } from 'lucide-react';

/**
 * Event Popup Component
 * @param {Object} props
 * @param {Date} props.date
 * @param {import('../types/calendar').CalendarEvent[]} props.events
 * @param {import('../types/calendar').CalendarArea[]} props.areas
 * @param {function} props.onClose
 */
const EventPopup = ({ date, events, areas, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-5 h-5" />
              <h2 className="text-lg font-semibold">
                {date.toLocaleDateString('en', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {events.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CalendarIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              No events scheduled for this date
            </div>
          ) : (
            <div className="space-y-4">
              {events.map((event) => {
                const area = areas.find((a) => a.id === event.area);
                return (
                  <div
                    key={event.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    {/* Event Title + Area */}
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 flex-1">
                        {event.title}
                      </h3>
                      <span
                        className={`px-2 py-1 rounded-full text-xs text-white ml-2 ${area?.color || 'bg-gray-500'}`}
                      >
                        {area?.name || 'Unknown'}
                      </span>
                    </div>

                    {/* Event Details */}
                    <div className="space-y-2 mb-3 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        {event.time || `${new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(event.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                      </div>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {area?.name || 'Location TBD'}
                      </div>
                      {event.attendees && (
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2" />
                          {event.attendees} expected attendees
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {event.description && (
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {event.description}
                      </p>
                    )}

                    {/* External Link */}
                    {event.link && (
                      <a
                        href={event.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                      >
                        More details
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {events.length > 0 && (
          <div className="bg-gray-50 p-4 border-t text-sm text-gray-600 text-center">
            {events.length} event{events.length !== 1 ? 's' : ''} scheduled
          </div>
        )}
      </div>
    </div>
  );
};

export default EventPopup;
